import { useSyncExternalStore } from 'react';

// Installing the app, and the service worker that makes it work closed and offline.

type InstallPrompt = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const RELOADED_KEY = 'dvf:reloaded-for-new-version';

let waitingPrompt: InstallPrompt | null = null;
let justInstalled = false;
const listeners = new Set<() => void>();
const changed = () => {
  for (const listener of listeners) listener();
};

/** Runs before anything is drawn: the browser offers the install only once, early on. */
export function startPwa(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    waitingPrompt = event as InstallPrompt;
    changed();
  });
  window.addEventListener('appinstalled', () => {
    waitingPrompt = null;
    justInstalled = true;
    changed();
  });
  // A page open across a deploy asks for a file that is gone: the new version has it. Only once,
  // and never offline, where a reload would not help and could go on for ever.
  window.addEventListener('vite:preloadError', () => {
    if (!navigator.onLine || sessionStorage.getItem(RELOADED_KEY)) return;
    sessionStorage.setItem(RELOADED_KEY, '1');
    window.location.reload();
  });
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    void navigator.serviceWorker.register('/sw.js');
  }
}

export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const isIos = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export async function promptInstall(): Promise<void> {
  const prompt = waitingPrompt;
  if (!prompt) return;
  await prompt.prompt();
  await prompt.userChoice;
  waitingPrompt = null;
  changed();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Installed already, can be installed with a click, or neither (the browser decides). */
export function useInstallState(): 'installed' | 'ready' | 'manual' {
  return useSyncExternalStore(subscribe, () =>
    justInstalled || isStandalone() ? 'installed' : waitingPrompt ? 'ready' : 'manual',
  );
}
